import type { Dispatch, FC, SetStateAction } from 'react';
import { Fragment, useState } from 'react';
import {
  TableCell,
  TableRow,
  TableBody,
  Stack,
  Typography,
  Checkbox,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  DeleteOutline,
  Download,
  PersonAdd,
  DescriptionOutlined,
  InsertDriveFileOutlined,
} from '@mui/icons-material';
import {
  TaskRo,
  useTasksControllerRemoveMutation,
  useTasksControllerGenerateOutputMutation,
} from 'src/app/services/api.generated';
import { useTranslation } from 'react-i18next';
import { taskGetComparator } from 'src/utils/sortTasksUtils';
import { usernameDisplayName } from 'src/utils/displayUsername';
import { useAppDispatch } from 'src/app/reduxCustomHooks';
import moment from 'moment';
import { TaskTableSkeleton } from './TaskTableSkeleton';
import { toast } from 'react-toastify';
import { ConfirmDialog } from 'src/components/molecules/ConfirmDialog';
import { AssignTaskDialog } from 'src/components/molecules/AssignTaskDialog';
import { RowActions } from 'src/components/molecules/RowActions';
import {
  TaskAnalysisStatusChip,
  TaskOutputStatusChip,
} from 'src/components/molecules/TaskStatusChips';
import { useNavigate } from 'react-router';
import { tableOrderType } from 'src/types/generalTypes';
import { api } from 'src/app/services/api';
import {
  isOpenableTaskState,
  patchTaskOutputState,
  TaskWithProcessingProgress,
  WorkspaceFolderQueryKey,
} from 'src/utils/taskProgressStreamUtils';
import { isExportOutdatedError } from 'src/utils/exportOutdated';
import { MANUAL_CHECK_KEYS, manualCheckPagesOf } from 'src/utils/manualCheck';

type TasksTableBodyPropsType = {
  rows: TaskWithProcessingProgress[];
  order: tableOrderType;
  orderBy: keyof TaskRo;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  showCheckbox: boolean;
  folderQueryKey?: WorkspaceFolderQueryKey;
};

export const TasksTableBody: FC<TasksTableBodyPropsType> = ({
  rows,
  order,
  orderBy,
  page,
  rowsPerPage,
  isLoading,
  selected,
  setSelected,
  showCheckbox,
  folderQueryKey,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TaskRo | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [removeTask, { isLoading: removeTaskLoading }] =
    useTasksControllerRemoveMutation();
  const [generateOutput, { isLoading: generateOutputLoading }] =
    useTasksControllerGenerateOutputMutation();

  const openConfirmDialog = () => setShowConfirmDialog(true);
  const closeConfirmDialog = () => setShowConfirmDialog(false);

  const isSelected = (id: string) => selected.includes(id);

  const checkboxOnChange = (id: string) => {
    setSelected((prevState) => {
      if (prevState.includes(id)) {
        return prevState.filter((item) => item !== id);
      }
      return [...prevState, id];
    });
  };

  const removeTaskHandler = () => {
    if (!selectedRow) return;
    removeTask({ idDto: { id: selectedRow.id } })
      .unwrap()
      .then(() => {
        toast.success(t('taskSuccessfullyDeleted'));
        closeConfirmDialog();
      });
  };

  const downloadOrGenerateHandler = async (taskRow: TaskRo) => {
    if (taskRow.outputState === 'GENERATED_OUTPUT') {
      try {
        const outFileUrl = await dispatch(
          api.endpoints.tasksControllerGetTaskOutFileUrl.initiate(
            { taskId: taskRow.id },
            { forceRefetch: true }
          )
        ).unwrap();
        if (!outFileUrl?.url) {
          toast.error(t('somethingWentWrong'));
          return;
        }
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);
        anchor.href = outFileUrl.url;
        anchor.download =
          taskRow.name?.split('.').slice(0, -1).join('.') +
            `(${t('redacted')})` || 'outputFile';
        anchor.click();
        anchor.remove();
      } catch (error) {
        if (isExportOutdatedError(error)) {
          // Edited after the export: the row offers "anonymize" again.
          if (folderQueryKey) {
            patchTaskOutputState(
              dispatch,
              folderQueryKey,
              taskRow.id,
              'OUTDATED_OUTPUT'
            );
          }
          toast.warning(t('exportOutdated'));
          return;
        }
        toast.error(t('somethingWentWrong'));
      }
    } else {
      if (folderQueryKey) {
        patchTaskOutputState(
          dispatch,
          folderQueryKey,
          taskRow.id,
          'GENERATING_OUTPUT'
        );
      }
      generateOutput({ generateOutputDto: { id: taskRow.id } })
        .unwrap()
        .then(() => {
          toast.info(t('generateOutputReq'));
          // Warn only: the export is not blocked.
          const flagged = manualCheckPagesOf(taskRow);
          if (flagged > 0) {
            toast.warning(t(MANUAL_CHECK_KEYS.exportWarning, { n: flagged }));
          }
        });
    }
  };

  const stateRenderHandler = (row: TaskWithProcessingProgress) => (
    <TaskAnalysisStatusChip
      state={row.state}
      processingProgress={row.processingProgress}
      stuckStage={row.stuckStage}
    />
  );

  const outputStateRenderHandler = (
    outputState: TaskRo['outputState'],
    stuckStage?: TaskRo['stuckStage']
  ) => (
    <TaskOutputStatusChip outputState={outputState} stuckStage={stuckStage} />
  );

  const rowOnClick = (taskRow: TaskRo) => {
    const state: { folderId?: string; workspaceId?: string } = {};
    if (folderQueryKey?.endpoint === 'workspacesControllerGetFolderById') {
      state.folderId = folderQueryKey.args.folderId;
      state.workspaceId = folderQueryKey.args.workspaceId;
    } else if (
      folderQueryKey?.endpoint === 'workspacesControllerGetRootFolder'
    ) {
      state.workspaceId = folderQueryKey.args.id;
    } else if (taskRow.workspace?.id) {
      state.workspaceId = taskRow.workspace.id;
    }
    const archiveFolderId = (taskRow as TaskRo & { folderId?: string | null })
      .folderId;
    if (archiveFolderId) {
      state.folderId = archiveFolderId;
    }
    navigate('/edit/' + taskRow.id, { state });
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const resolveWorkspaceId = (row: TaskRo) => {
    if (folderQueryKey?.endpoint === 'workspacesControllerGetRootFolder') {
      return folderQueryKey.args.id;
    }
    if (folderQueryKey?.endpoint === 'workspacesControllerGetFolderById') {
      return folderQueryKey.args.workspaceId;
    }
    return row.workspace?.id;
  };

  return (
    <>
      <TableBody>
        {isLoading ? (
          <TaskTableSkeleton
            showCheckbox={showCheckbox}
            rowCount={rowsPerPage}
            cellCount={5}
          />
        ) : (
          [...rows]
            .sort(taskGetComparator(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row: TaskRo, index: number) => {
              const { id, creator, assigneeId, name, createdAt, userDisplay } =
                row;
              const isItemSelected = isSelected(id);
              const realIndex = page * rowsPerPage + index;
              const labelId = `enhanced-table-checkbox-${realIndex}`;
              const outputReady = row.outputState === 'GENERATED_OUTPUT';
              const manualCheckPages = manualCheckPagesOf(row);
              const canGenerate =
                row.state === 'READY' && row.outputState !== 'GENERATED_OUTPUT';
              const openable = isOpenableTaskState(row.state);

              return (
                <Fragment key={realIndex}>
                  <TableRow
                    hover
                    tabIndex={openable ? 0 : -1}
                    aria-labelledby={labelId}
                    sx={(theme) => ({
                      cursor: openable ? 'pointer' : 'default',
                      '& > td': {
                        py: 0.5,
                        px: 1.25,
                        height: 44,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      },
                      '&:last-of-type > td': { borderBottom: 'none' },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: -2,
                      },
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (openable) {
                        rowOnClick(row);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Only the row itself: Enter on a button inside must not open the task.
                      if (
                        openable &&
                        e.target === e.currentTarget &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        rowOnClick(row);
                      }
                    }}
                  >
                    {showCheckbox && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            checkboxOnChange(id);
                          }}
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="left" id={labelId}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1.25}
                        sx={{ minWidth: 0 }}
                      >
                        <InsertDriveFileOutlined
                          fontSize="small"
                          aria-hidden
                          sx={{ color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Tooltip title={name}>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: 500,
                              maxWidth: { xs: 140, md: 200, lg: 320 },
                            }}
                          >
                            {name}
                          </Typography>
                        </Tooltip>
                        {manualCheckPages > 0 && (
                          <Chip
                            size="small"
                            data-testid={`manual-check-chip-${id}`}
                            label={t(MANUAL_CHECK_KEYS.listChip, {
                              n: manualCheckPages,
                            })}
                            sx={{
                              flexShrink: 0,
                              height: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              color: 'review.notice.text',
                              bgcolor: 'review.notice.bg',
                              border: '1px solid',
                              borderColor: 'review.notice.border',
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="left">
                      <Typography
                        variant="body2"
                        noWrap
                        title={usernameDisplayName(creator, userDisplay) || undefined}
                        sx={{ maxWidth: 140 }}
                      >
                        {usernameDisplayName(creator, userDisplay) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography
                        variant="body2"
                        noWrap
                        color={assigneeId ? 'text.primary' : 'text.secondary'}
                        sx={{ maxWidth: 140 }}
                      >
                        {assigneeId
                          ? usernameDisplayName(assigneeId, userDisplay) || '—'
                          : t('noAssignee')}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Tooltip title={moment(createdAt).format('LLLL')}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {moment(createdAt).format('YYYY/MM/DD')}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">
                      {stateRenderHandler(row)}
                    </TableCell>
                    <TableCell align="left">
                      {outputStateRenderHandler(row.outputState, row.stuckStage)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', width: '1%' }}
                    >
                      <RowActions
                        actions={[
                          {
                            id: 'assign',
                            label: t('assign'),
                            icon: <PersonAdd fontSize="small" />,
                            disabled: !resolveWorkspaceId(row),
                            onClick: (e) => {
                              e.stopPropagation();
                              setSelectedRow(row);
                              setShowAssignDialog(true);
                            },
                          },
                          {
                            id: outputReady ? 'download' : 'generateOutput',
                            label: t(
                              outputReady ? 'download' : 'generateOutput'
                            ),
                            icon: outputReady ? (
                              <Download fontSize="small" />
                            ) : (
                              <DescriptionOutlined fontSize="small" />
                            ),
                            disabled: !outputReady && !canGenerate,
                            loading:
                              generateOutputLoading && selectedRow?.id === id,
                            onClick: (e) => {
                              e.stopPropagation();
                              setSelectedRow(row);
                              downloadOrGenerateHandler(row);
                            },
                          },
                          {
                            id: 'delete',
                            label: t('delete'),
                            icon: <DeleteOutline fontSize="small" />,
                            destructive: true,
                            onClick: (e) => {
                              e.stopPropagation();
                              setSelectedRow(row);
                              openConfirmDialog();
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })
        )}
        {emptyRows > 0 &&
          [...Array(emptyRows)].map((_, index) => (
            <TableRow key={index}>
              <TableCell colSpan={showCheckbox ? 8 : 7} sx={{ height: 44 }} />
            </TableRow>
          ))}
      </TableBody>
      <ConfirmDialog
        destructive
        dialogProps={{
          open: showConfirmDialog,
          onClose: closeConfirmDialog,
        }}
        warningText={t('confirmDeleteTaskNoWorkspace', {
          taskName: selectedRow !== null ? selectedRow.name : '',
        })}
        onSubmitClick={removeTaskHandler}
        isSubmitLoading={removeTaskLoading}
        submitText={t('delete')}
      />
      {selectedRow && (
        <AssignTaskDialog
          open={showAssignDialog}
          onClose={() => setShowAssignDialog(false)}
          taskId={selectedRow.id}
          workspaceId={resolveWorkspaceId(selectedRow)}
          currentAssigneeId={selectedRow.assigneeId}
        />
      )}
    </>
  );
};
